import { EncodeArrayBufferToBase64 } from '@babylonjs/core/Misc/stringTools';
import type { ResourceManager } from '../loaders/ResourceManager';
import { fileExtensionFromUri } from '../loaders/formats/gltf21/normalize';
import { isHtmlDocument, isPngBlob, mimeTypeFromExtension } from '../utils/AssetTypes';

interface ExternalFile { uri?: string; mimeType?: string }
interface ModelDocument {
    asset?: { version?: string }; files?: unknown[]; externalAssets?: unknown[];
    buffers?: ExternalFile[]; images?: ExternalFile[];
}

/** URL parser preprocessing, for ownership only; authored/cache-key bytes stay unchanged.
 * https://url.spec.whatwg.org/#concept-basic-url-parser
 */
function normalizedUriForClassification(uri: string): string {
    // eslint-disable-next-line no-control-regex -- URL preprocessing removes exactly the C0/space range.
    return uri.replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '').replace(/[\t\n\r]/g, '');
}

async function imageMimeType(blob: Blob, entry: ExternalFile): Promise<string> {
    if (await isPngBlob(blob)) return 'image/png';
    const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
    if (head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return 'image/jpeg';
    const text = new TextDecoder().decode(head);
    if (text.startsWith('RIFF') && text.slice(8, 12) === 'WEBP') return 'image/webp';
    if ([0xab, 0x4b, 0x54, 0x58, 0x20, 0x32, 0x30, 0xbb, 0x0d, 0x0a, 0x1a, 0x0a]
        .every((byte, i) => head[i] === byte)) return 'image/ktx2';
    if (text.slice(4, 8) === 'ftyp' && /^(avif|avis)$/.test(text.slice(8, 12))) return 'image/avif';
    const mime = [entry.mimeType, blob.type, mimeTypeFromExtension(fileExtensionFromUri(entry.uri!))]
        .map(value => value?.split(';')[0].trim().toLowerCase()).find(value => value?.startsWith('image/'));
    if (!mime) throw new Error('cannot determine the image MIME type');
    return mime;
}

/** Embed exact companion bytes in an export copy; folder uploads may rename the model itself. */
export async function inlineGltfExternalAssets(path: string, blob: Blob, resources: ResourceManager): Promise<Blob> {
    if (fileExtensionFromUri(path) !== 'gltf' && blob.type !== 'model/gltf+json') return blob;
    let document: ModelDocument;
    try {
        document = JSON.parse(await blob.text()) as ModelDocument;
        if (!document || typeof document !== 'object' || Array.isArray(document)) throw new Error();
    } catch {
        throw new Error(`Cannot package glTF ${path}: expected a JSON model object`);
    }
    // Trace documents have their own manifest and reference contract.
    if (document.asset?.version !== '2.0' || document.files || document.externalAssets) return blob;
    const entries = [
        ...(document.buffers ?? []).map(entry => ({ entry, kind: 'buffer' as const })),
        ...(document.images ?? []).map(entry => ({ entry, kind: 'image' as const })),
    ].filter(({ entry }) => {
        if (typeof entry.uri !== 'string' || !entry.uri) return false;
        const uri = normalizedUriForClassification(entry.uri);
        return /^blob:/i.test(uri) || !/^(?:[a-z][a-z\d+.-]*:|[\\/]{2})/i.test(uri);
    });
    if (entries.length === 0) return blob;

    const base = resources.getAssetBaseUrl(path);
    const source = resources.resolveAssetUrl(path) ?? resources.getAssetSourceUrl(path)
        ?? (/^https?:\/\//.test(path) ? path : undefined);
    const encoded = new Map<string, Promise<string>>();
    await Promise.all(entries.map(async ({ entry, kind }) => {
        const uri = entry.uri!;
        const cacheKey = `${kind}:${entry.mimeType ?? ''}:${uri}`;
        let pending = encoded.get(cacheKey);
        if (!pending) {
            pending = (async () => {
                const isBlob = /^blob:/i.test(normalizedUriForClassification(uri));
                let companion = isBlob ? resources.getAsset(uri) : resources.resolveExternalAssetBlob(uri, path);
                if (!companion) {
                    const remote = isBlob ? new URL(uri).href
                        : base || source ? new URL(uri, base ?? source).href : undefined;
                    if (!remote) throw new Error('no registered companion or source directory');
                    const response = await fetch(remote);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    companion = await response.blob();
                }
                const body = await companion.arrayBuffer();
                if (isHtmlDocument(companion.type, body)) throw new Error('received an HTML page instead of companion bytes');
                const mime = kind === 'buffer' ? 'application/octet-stream' : await imageMimeType(companion, entry);
                return `data:${mime};base64,${EncodeArrayBufferToBase64(body)}`;
            })();
            encoded.set(cacheKey, pending);
        }
        try {
            entry.uri = await pending;
        } catch (error) {
            throw new Error(`Cannot package glTF ${path}: companion ${uri}: ${error instanceof Error ? error.message : String(error)}`);
        }
    }));
    return new Blob([JSON.stringify(document)], { type: blob.type || 'model/gltf+json' });
}
